/**
 * Pure arbitration helpers that collect the typeahead providers from the mounted
 * plugins, so the engine has a flat list to run.
 *
 * Everything here keeps "registration order": plugins in the order passed to
 * `<Composer plugins={…} />`, and within each plugin, providers in the order of its
 * `typeahead` array. When several providers share one trigger, their sections
 * render in that same order.
 */
/**
 * Every enabled typeahead provider across the mounted plugins, in registration order.
 *
 * @param previous Previously collected providers whose unchanged references can be reused.
 * Omit for a fresh registry; live refreshes pass the preceding collection.
 */
export function collectProviders(plugins, previous = []) {
    return plugins.flatMap((plugin) => (plugin.typeahead ?? []).flatMap((provider, index) => {
        if (provider.enabled === false)
            return [];
        const id = `${plugin.id}:${index}`;
        // Unrelated plugin refreshes must not restart this provider's query or reset its highlight.
        const unchanged = previous.find((candidate) => candidate.registration?.id === id && candidate.registration.source === provider);
        return (unchanged ?? { ...provider, registration: { id, pluginId: plugin.id, source: provider } });
    }));
}
/** The distinct triggers the providers answer; the engine hands this set to `detectActiveTrigger`. */
export function registeredTriggers(providers) {
    return [...new Set(providers.map((provider) => provider.trigger))];
}
/**
 * The providers that own `prefix`, in registration order. Getting more than one
 * back is the merged/sectioned case (e.g. Spaces + Files under `@`): the engine
 * renders each provider's results under its own `section`, in that order.
 */
export function selectProviders(providers, prefix) {
    return providers.filter((provider) => provider.trigger === prefix);
}
/**
 * The providers that own `prefix` and answer `query` (`RegisteredTypeaheadProvider.matchQuery`), in
 * registration order. A provider without `matchQuery` answers every query. An empty result means
 * nobody on the trigger wants this query, and the engine treats the trigger as inactive.
 */
export function claimingProviders(providers, prefix, query) {
    return selectProviders(providers, prefix).filter((provider) => provider.matchQuery === undefined || provider.matchQuery(query));
}
/** Whether both runs see the same providers, by reference and order. */
export function haveSameProviders(previous, current) {
    return (previous.length === current.length &&
        previous.every((provider, index) => provider === current[index]));
}
//# sourceMappingURL=arbitration.js.map